import { useMemo } from "react"

import { useI18next } from "gatsby-plugin-react-i18next"
import styled, { css } from "styled-components"

import GeneralCard from "components/Cards/GeneralCard"
import InnerWrapper from "components/InnerWrapper"
import Callout from "components/TextElements/Callout"
import { KickerTextType } from "components/TextElements/Kicker"
import ZoomImageGatsby from "components/UI-Related/ZoomImageGatsby"
import colors from "styles/colors"
import text from "styles/text"
import { fmobile, fresponsive, ftablet } from "utils/fullyResponsive"
import { checkedLocaleLink } from "utils/routes"
import useMedia from "utils/useMedia"

interface Props extends PageProps {
  allAnimationImages: Record<string, IGatsbyImageData | undefined>
}

export default function PeopleAchieve({ allAnimationImages }: Props) {
  const titleWidth = useMedia("517px", "31.145vw", "47.461vw", "89.333vw")
  const textWidth = useMedia("516px", "31.084vw", "33.984vw", "89.333vw")
  const imageWidth = useMedia("905px", "54.518vw", "81.445vw", "89.333vw")
  const imageHeight = useMedia("519px", "31.265vw", "29.883vw", "81.6vw")
  const imageBorderR = useMedia("20px", "1.205vw", "20px", "20px")
  const borderVal = useMedia(false, false, true, true)
  const flex = useMedia(false, false, true, false)
  const { t, language } = useI18next()
  const { ourPeople } = allAnimationImages

  const cardData: {
    kickerIcon: KickerTextType
    title: string
    text: string
    url: string
  }[] = useMemo(
    () => [
      {
        title: t("Banking"),
        text: t(
          "Launch and grow your neobank, whether you’re a fintech startup, a financial institution or an established brand looking to explore new digital banking opportunities."
        ),
        kickerIcon: "neobanking",
        url: "/payments-solutions/digital-banking-platform",
      },
      {
        title: t("B2B Payments"),
        text: t(
          "Build a business payments platform that gives your customers flexible, secure commercial payments."
        ),
        kickerIcon: "b2b",
        url: "/payments-solutions/b2b-payments",
      },
      {
        title: t("Growth & Activation"),
        text: t(
          "Achieve your program’s revenue goals by helping your customers instantly activate cards, start spending as soon as possible–and stay with you for the long haul."
        ),
        kickerIcon: "growth",
        url: "/payments-solutions/growth-and-activation",
      },
      {
        title: t("Cryptocurrency"),
        text: t(
          "Simplify payments and bridge the gap between today’s mainstream payment rails and tomorrow’s cryptocurrency wallets."
        ),
        kickerIcon: "crypto",
        url: "/payments-solutions/cryptocurrency",
      },
      {
        title: t("Embedded Finance"),
        text: t(
          "Improve the customer experience and maintain those relationship by tying together all of the components of embedded finance."
        ),
        kickerIcon: "embedded",
        url: "/payments-solutions/embedded-finance",
      },
      {
        title: t("Investing"),
        text: t(
          "Boost client loyalty by offering spending capabilities attached to your customers' investing and brokerage accounts."
        ),
        kickerIcon: "investing",
        url: "/payments-solutions/investing-and-cash-management",
      },
    ],
    [t]
  )

  const allCards = useMemo(
    () =>
      cardData.map(card => {
        const localeLink = checkedLocaleLink(card.url, language)

        const cardInfo = {
          icon: card.kickerIcon,
          title: card.title,
          body: card.text,
          link: localeLink,
          border: borderVal,
        }

        return <GeneralCard key={`card_${card.title}`} info={cardInfo} />
      }),
    [borderVal, cardData, language]
  )

  return (
    <Wrapper>
      <InnerWrapper>
        <ImageWrapper>
          {ourPeople && (
            <ZoomImageGatsby
              src={ourPeople}
              alt={t("two men standing at a whiteboard")}
              width={imageWidth}
              height={imageHeight}
              borderRadius={imageBorderR}
            />
          )}
        </ImageWrapper>
        <Callout
          title={{
            content: t("What can we achieve together?"),
            width: titleWidth,
            size: useMedia(text.h3, text.h3, text.h4, text.h1Mobile),
          }}
          text={{
            content: t(
              "We've spent 20+ years tackling financial technology challenges with early-stage start ups, emerging companies and established brands to launch and scale innovative payment processing and digital banking programs. No matter how you want to invent the financial experience, we're with you on the journey, so you can think boldly and deliver big."
            ),
            width: textWidth,
            color: colors.blue200,
          }}
          kicker={{
            text: t("OUR PEOPLE") ?? "OUR PEOPLE",
            icon: "slices",
            color: colors.blue200,
          }}
          flex={flex}
        />
        <CardsSection>{allCards}</CardsSection>
      </InnerWrapper>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  background: ${colors.lightMode};
  will-change: transform;
  transform: translate3d(0, 0, 0, 0);
  padding: 9.036vw 0 0;
  overflow: hidden;

  ${fresponsive(css`
    padding: 150px 0 67px;
  `)}

  ${ftablet(css`
    padding: 150px 0 150px;
  `)}

  ${fmobile(css`
    padding: 60px 20px 80px 20px;
  `)}
`
const ImageWrapper = styled.div`
  position: absolute;
  z-index: -1;

  ${fresponsive(css`
    top: -100px;
    left: 536px;
  `)}

  ${ftablet(css`
    position: relative;
    top: auto;
    left: auto;
    margin-bottom: 80px;
  `)}

  ${fmobile(css`
    position: relative;
    left: 0;
    margin-bottom: 40px;
  `)}
`

const CardsSection = styled.div`
  display: grid;
  position: relative;
  width: 100%;

  ${fresponsive(css`
    grid-template-columns: 1fr 1fr 1fr;
    margin-top: 189px;
    grid-column-gap: 60px;
    grid-template-rows: 360px;
    grid-row-gap: 80px;
  `)}

  ${ftablet(css`
    grid-template-columns: 1fr 1fr;
    margin-top: 80px;
    grid-column-gap: 20px;
    grid-template-rows: 360px;
    grid-row-gap: 40px;
  `)}

  ${fmobile(css`
    display: flex;
    flex-direction: column;
    gap: 20px;
  `)}
`
